import type { GroupSummary } from "@kaidoki/proto";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { isLoggedIn } from "../lib/auth";

// Module-level ref for non-React code (e.g., api/client.ts)
let _activeGroupIdRef: string | null = null;

export function getActiveGroupIdRef(): string | null {
  return _activeGroupIdRef;
}

interface GroupState {
  groups: GroupSummary[];
  activeGroupId: string | null;
  loading: boolean;
  initialized: boolean;
}

type GroupAction =
  | { type: "SET_GROUPS"; groups: GroupSummary[]; activeGroupId?: string }
  | { type: "SET_ACTIVE"; groupId: string }
  | { type: "ADD_GROUP"; group: GroupSummary; setActive?: boolean }
  | { type: "REMOVE_GROUP"; groupId: string }
  | { type: "UPDATE_NAME"; groupId: string; name: string }
  | { type: "CLEAR" }
  | { type: "SET_LOADING"; loading: boolean };

function groupReducer(state: GroupState, action: GroupAction): GroupState {
  switch (action.type) {
    case "SET_GROUPS": {
      const activeGroupId =
        action.activeGroupId ??
        (action.groups.length > 0 ? action.groups[0].id : null);
      return {
        ...state,
        groups: action.groups,
        activeGroupId,
        loading: false,
        initialized: true,
      };
    }
    case "SET_ACTIVE":
      return { ...state, activeGroupId: action.groupId };
    case "ADD_GROUP": {
      const groups = [...state.groups, action.group];
      return {
        ...state,
        groups,
        activeGroupId:
          action.setActive !== false ? action.group.id : state.activeGroupId,
      };
    }
    case "REMOVE_GROUP": {
      const groups = state.groups.filter((g) => g.id !== action.groupId);
      const activeGroupId =
        state.activeGroupId === action.groupId
          ? groups.length > 0
            ? groups[0].id
            : null
          : state.activeGroupId;
      return { ...state, groups, activeGroupId };
    }
    case "UPDATE_NAME":
      return {
        ...state,
        groups: state.groups.map((g) =>
          g.id === action.groupId ? { ...g, name: action.name } : g,
        ),
      };
    case "CLEAR":
      return {
        groups: [],
        activeGroupId: null,
        loading: false,
        initialized: false,
      };
    case "SET_LOADING":
      return { ...state, loading: action.loading };
  }
}

interface GroupContextValue {
  groups: GroupSummary[];
  activeGroupId: string | null;
  loading: boolean;
  initialized: boolean;
  setActiveGroup: (groupId: string) => void;
  addGroup: (group: GroupSummary, setActive?: boolean) => void;
  removeGroup: (groupId: string) => void;
  updateGroupName: (groupId: string, name: string) => void;
  clearGroups: () => void;
  initializeGroups: (groups: GroupSummary[], activeGroupId?: string) => void;
}

const GroupContext = createContext<GroupContextValue | null>(null);

export function GroupProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(groupReducer, {
    groups: [],
    activeGroupId: null,
    loading: true,
    initialized: false,
  });

  // Sync module-level ref
  useEffect(() => {
    _activeGroupIdRef = state.activeGroupId;
  }, [state.activeGroupId]);

  // On mount: if not logged in, stop loading but keep initialized: false
  // If logged in, keep loading: true — AppInitializer will call initializeGroups()
  useEffect(() => {
    if (!isLoggedIn()) {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  }, []);

  const setActiveGroup = useCallback((groupId: string) => {
    dispatch({ type: "SET_ACTIVE", groupId });
  }, []);

  const addGroup = useCallback((group: GroupSummary, setActive?: boolean) => {
    dispatch({ type: "ADD_GROUP", group, setActive });
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    dispatch({ type: "REMOVE_GROUP", groupId });
  }, []);

  const updateGroupName = useCallback((groupId: string, name: string) => {
    dispatch({ type: "UPDATE_NAME", groupId, name });
  }, []);

  const clearGroups = useCallback(() => {
    dispatch({ type: "CLEAR" });
  }, []);

  const initializeGroups = useCallback(
    (groups: GroupSummary[], activeGroupId?: string) => {
      dispatch({ type: "SET_GROUPS", groups, activeGroupId });
    },
    [],
  );

  return (
    <GroupContext.Provider
      value={{
        groups: state.groups,
        activeGroupId: state.activeGroupId,
        loading: state.loading,
        initialized: state.initialized,
        setActiveGroup,
        addGroup,
        removeGroup,
        updateGroupName,
        clearGroups,
        initializeGroups,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
}

export function useGroup(): GroupContextValue {
  const ctx = useContext(GroupContext);
  if (!ctx) {
    throw new Error("useGroup must be used within GroupProvider");
  }
  return ctx;
}
